/**
 * ConnectorsPage — top-level connector management page (#67 redesign).
 *
 * Single dense, sortable, searchable, filterable table. Aggregate statistics
 * live at /connectors/statistics. Adding a connector navigates to the
 * full-page wizard at /connectors/new (#69).
 */
import React, { useCallback, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/useToast';
import { useConnectors } from '@/hooks/useConnectors';
import type { Connector } from '@/types/connectors';
import { saveLastLibraryUrl } from '@/lib/lastLibraryUrl';

import { ConnectorTable } from '@/components/connectors/library';

export const ConnectorsPage: React.FC = () => {
  const { toast } = useToast();
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    saveLastLibraryUrl(`${loc.pathname}${loc.search}`);
  }, [loc.pathname, loc.search]);
  const { isLoading, refetch } = useConnectors() as any;

  const handleOpen = useCallback((_c: Connector) => {
    // Row clicks navigate to the detail page via ConnectorTable's onRowClick.
  }, []);

  const handleAddConnector = useCallback(() => {
    navigate('/connectors/new');
  }, [navigate]);

  const handleRefresh = useCallback(async () => {
    try {
      await refetch();
      toast({ title: 'Refreshed', description: 'Connectors list refreshed.' });
    } catch (err) {
      toast({
        title: 'Refresh failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [refetch, toast]);

  return (
    <div className="container mx-auto px-6 py-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Connectors</h1>
          <p className="text-muted-foreground mt-1">Manage your data source connectors</p>
          {/* react-router Links, not bare <a href>: the JWT lives only in
              memory, so a full page load logs the operator out and bounces
              them to /login (#946, #1029). */}
          <div className="mt-1 flex gap-4">
            <Link
              to="/connectors/statistics"
              className="inline-block text-sm text-primary hover:underline"
            >
              View statistics →
            </Link>
            <Link
              to="/connectors/schedule"
              className="inline-block text-sm text-primary hover:underline"
            >
              View schedule timeline →
            </Link>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={handleAddConnector} size="lg">
            <Plus className="h-4 w-4 mr-2" />
            Add Connector
          </Button>
        </div>
      </div>

      <ConnectorTable onOpenConnector={handleOpen} onAddConnector={handleAddConnector} />
    </div>
  );
};
